/**
 * Compliance & Standards — regulatory context, certifications,
 * regulatory requirements, privacy compliance. Pulls from
 * `formatted.compliance_and_standards`. Always renders.
 */
import React from 'react';
import { SectionCard, SubSection, StringListField, ObjectListField, type SectionProps } from './parts';

const PATH = ['compliance_and_standards'];

const yesNo = (v) => (v === true ? 'true' : v === false ? 'false' : '');
const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v));

export default function ComplianceStandards({ data, isEditing, onChange }: SectionProps) {
  const cs = data ?? {};
  const regulatoryContext = cs.regulatory_context ?? [];
  const certifications = cs.standard_certifications?.certifications ?? [];
  const requirements = cs.regulatory_requirements?.requirements ?? [];
  const privacy = cs.privacy_compliance?.regulations ?? [];

  const certColumns = [
    { key: 'standard_name', label: 'Standard' },
    { key: 'certification_status', label: 'Status' },
    { key: 'certification_body', label: 'Body' },
    { key: 'target_date', label: 'Target Date' },
    { key: 'expiry_date', label: 'Expiry' },
    { key: 'scope', label: 'Scope' },
  ];
  const reqColumns = [
    { key: 'requirement_type', label: 'Type' },
    { key: 'description', label: 'Description' },
    { key: 'applicable_regions', label: 'Regions', render: joinList },
    { key: 'compliance_status', label: 'Status' },
    { key: 'reference', label: 'Reference' },
  ];
  const privacyColumns = [
    { key: 'regulation', label: 'Regulation' },
    { key: 'applicable', label: 'Applicable', render: yesNo },
    { key: 'data_types_covered', label: 'Data Types', render: joinList },
    { key: 'compliance_measures', label: 'Compliance Measures', render: joinList },
    { key: 'data_subject_rights', label: 'Subject Rights', render: joinList },
  ];

  return (
    <SectionCard title="Compliance & Standards" id="compliance_and_standards">
      <SubSection title="Regulatory context">
        <StringListField label="Regulatory context" items={regulatoryContext} isEditing={isEditing} onChange={onChange} path={[...PATH, 'regulatory_context']} />
      </SubSection>

      <SubSection title="Certifications" count={certifications.length}>
        <ObjectListField columns={certColumns} rows={certifications} isEditing={isEditing} onChange={onChange} path={[...PATH, 'standard_certifications', 'certifications']} />
      </SubSection>

      <SubSection title="Regulatory requirements" count={requirements.length}>
        <ObjectListField columns={reqColumns} rows={requirements} isEditing={isEditing} onChange={onChange} path={[...PATH, 'regulatory_requirements', 'requirements']} />
      </SubSection>

      <SubSection title="Privacy compliance" count={privacy.length}>
        <ObjectListField columns={privacyColumns} rows={privacy} isEditing={isEditing} onChange={onChange} path={[...PATH, 'privacy_compliance', 'regulations']} />
      </SubSection>
    </SectionCard>
  );
}
